'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './TabletCaseStudy.module.css';

interface Integration {
  name: string;
  logo: string;
  category: string;
}

interface TabletCaseStudyItem {
  company: string;
  solution: string;
  location: string;
  images?: string[];
  videoWebm?: string;
  videoMp4?: string;
  description: string;
  integrations: Integration[];
}

export interface TabletCaseStudyProps {
  badge: string;
  title: string;
  subtitle: string;
  cases: TabletCaseStudyItem[];
  labels: {
    client: string;
    solution: string;
    location: string;
    integratedWith: string;
  };
  altTexts?: Record<string, string>;
}

/**
 * TabletCaseStudy Component
 * Displays tablet ordering case studies with image carousel
 */
export function TabletCaseStudy({ badge, title, subtitle, cases, labels, altTexts }: TabletCaseStudyProps) {
  const [activeImageIndices, setActiveImageIndices] = useState<Record<number, number>>({});
  const [isVisible, setIsVisible] = useState(false);

  // Auto-rotate images every 3.5 seconds for each case
  useEffect(() => {
    const intervals: NodeJS.Timeout[] = [];

    cases.forEach((caseItem, caseIndex) => {
      if (caseItem.images && caseItem.images.length > 1) {
        const interval = setInterval(() => {
          setActiveImageIndices((prev) => ({
            ...prev,
            [caseIndex]: ((prev[caseIndex] || 0) + 1) % caseItem.images!.length,
          }));
        }, 3500);
        intervals.push(interval);
      }
    });

    return () => {
      intervals.forEach((interval) => clearInterval(interval));
    };
  }, [cases]);

  // Lazy loading observer for images
  useEffect(() => {
    if (!isVisible) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setIsVisible(true);
          }
        },
        { rootMargin: '300px' }  // Load section early for smooth experience
      );

      const element = document.getElementById('tablet-case-study');
      if (element) {
        observer.observe(element);
      }

      return () => {
        if (element) {
          observer.unobserve(element);
        }
      };
    }
  }, [isVisible]);

  return (
    <section id="tablet-case-study" className={styles.caseStudy}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.badge}>{badge}</div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        {cases.map((caseItem, caseIndex) => (
          <div key={caseIndex} className={`${styles.caseItem} ${caseIndex % 2 === 1 ? styles.reversed : ''}`}>
            <div className={styles.textContent}>
              <div className={styles.company}>
                <span className={styles.companyLabel}>{labels.client}</span>
                <span className={styles.companyName}>{caseItem.company}</span>
              </div>
              <div className={styles.solution}>
                <span className={styles.solutionLabel}>{labels.solution}</span>
                <span className={styles.solutionName}>{caseItem.solution}</span>
              </div>
              
              <div className={styles.descriptionWrapper}>
                <p className={styles.description}>{caseItem.description}</p>
                
                {caseItem.integrations && caseItem.integrations.length > 0 && (
                  <div className={styles.integrations}>
                    <h4 className={styles.integrationsTitle}>{labels.integratedWith}</h4>
                    <div className={styles.integrationLogos}>
                      {caseItem.integrations.map((integration, idx) => {
                        // Generate integration slug for URL
                        const slug = integration.name.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '');
                        
                        // List of partners that should go to /partners/ instead of /integrations/
                        const partners = [
                          'konacom', 'possystems', 'cecon', 'janssens-kassasystemen', 'ms-pos',
                          'adnamics', 'upta', 'kassanet-pieterse', 'opsomer', 'evers-kassasystemen',
                          'stn', 'kassanet-nuus', 'bork', 'prestop', 'acrelec', 'qiox',
                          'van-hessen' // Van Hessen is also a partner
                        ];
                        
                        // Determine if this is a partner or a real integration
                        const isPartner = partners.includes(slug);
                        const integrationUrl = isPartner ? `/partners/${slug}` : `/integrations/${slug}`;
                        
                          return (
                          <Link key={idx} href={integrationUrl} className={styles.integrationLogo} prefetch={true}>
                            <Image
                              src={integration.logo}
                              alt={altTexts?.[integration.name.toLowerCase().replace(/\s+/g, '-')] || `${integration.name} integration`}
                              width={120}
                              height={60}
                              style={{ objectFit: 'contain' }}
                            />
                            <span className={styles.integrationName}>{integration.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.imageContainer}>
              {isVisible && caseItem.images && caseItem.images.length > 0 ? (
                <div className={styles.tabletCarousel}>
                  {caseItem.images.map((imageSrc, imgIndex) => (
                    <div
                      key={imgIndex}
                      className={`${styles.tabletWrapper} ${
                        (activeImageIndices[caseIndex] || 0) === imgIndex ? styles.active : ''
                      }`}
                    >
                      <div className={styles.tabletFrame}>
                        <Image
                          src={imageSrc}
                          alt={`${caseItem.company} tablet ordering screenshot ${imgIndex + 1}`}
                          width={600}
                          height={800}
                          quality={90}
                          priority={caseIndex === 0 && imgIndex === 0}
                          loading={caseIndex === 0 && imgIndex === 0 ? 'eager' : 'lazy'}
                          className={styles.tabletImage}
                        />
                      </div>
                    </div>
                  ))}
                  
                  {/* Carousel dots */}
                  {caseItem.images.length > 1 && (
                    <div className={styles.carouselDots}>
                      {caseItem.images.map((_, idx) => (
                        <button
                          key={idx}
                          className={`${styles.dot} ${(activeImageIndices[caseIndex] || 0) === idx ? styles.activeDot : ''}`}
                          onClick={() => setActiveImageIndices((prev) => ({ ...prev, [caseIndex]: idx }))}
                          aria-label={`View screenshot ${idx + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
              <div className={styles.imageGlow} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

